import { CSSProperties } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import { mergeObjectOnly } from '@lumapps/utils/object/mergeObjectOnly';

import { WidgetFooterStyles, WidgetGlobalStyles } from '../types';
import { computeStyles } from './computeStyles';

/**
 * Get the style to apply (changes in hovered state).
 */
function getStyleToApply(style: WidgetFooterStyles | undefined, isHovered: boolean): WidgetFooterStyles | undefined {
    if (!style || isEmpty(style)) {
        return undefined;
    }

    if (isHovered && style.hover) {
        // Hovered footer should apply hover styles.
        return mergeObjectOnly(cloneDeep(style), style.hover) as any;
    }

    return style;
}

/**
 * Compute style attributes to convert them into CSS properties.
 */
export function computeFooterStyles(
    styles: WidgetFooterStyles | undefined,
    isHovered: boolean,
    parentStyle?: WidgetGlobalStyles | undefined,
): { main?: CSSProperties; wrapper?: CSSProperties; icon?: CSSProperties; label?: CSSProperties } {
    const stylesToApply = getStyleToApply(styles, isHovered);
    const main = computeStyles(stylesToApply?.main) || {};
    const mainDisplay = stylesToApply?.main?.display || 'block';
    const wrapper = computeStyles(stylesToApply?.wrapper);

    if (mainDisplay === 'block') {
        if (parentStyle?.borderBottomRightRadius) {
            main.borderBottomRightRadius = parentStyle.borderBottomRightRadius;
        }
        if (parentStyle?.borderBottomLeftRadius) {
            main.borderBottomLeftRadius = parentStyle.borderBottomLeftRadius;
        }

        /* By default, the footer uses the mode "block" (display = block), so the background is applied on the main
         * and we have to strip the wrapper background. */
        if (wrapper) {
            delete wrapper.backgroundColor;
            delete wrapper.backgroundImage;
        }
    } else if (main) {
        /* If the footer uses the mode "inline" (display = flex), we strip the main background. */
        delete main.backgroundColor;
        delete main.backgroundImage;
    }

    const icon = computeStyles(stylesToApply?.icon);
    const label = computeStyles(stylesToApply?.label);

    return { main, wrapper, icon, label };
}
